import { useMemo } from 'react';
import { ImagePlus } from 'lucide-react';
import ImageSlot from './ImageSlot';
import { parseArticle, articleHeading, type ArticleBlock } from './articleHtml';
import type { ContentImage, ContentPiece } from '../../lib/contentPieces';

/**
 * PiecePreview — the article as it will read, block by block, with the image
 * tooling from spec 4.C:
 *
 *  - a featured-image slot above the title (always present; empty = dashed)
 *  - with `insertMode` on, hover gutters between top-level blocks; clicking
 *    one drops a queued slot marker at that spot
 *  - slot markers render through ImageSlot (queued / options / selected)
 */

export interface PiecePreviewProps {
  piece: ContentPiece;
  html: string;
  images: ContentImage[];
  featured: ContentImage | null;
  insertMode: boolean;
  /** Everything that writes is disabled while a request is in flight. */
  busy: boolean;
  /** False once the piece is published or the job can no longer take images. */
  canEditSlots: boolean;
  onInsertAfter: (afterBlock: number, blocks: ArticleBlock[]) => void;
  onAddFeatured: () => void;
  onRemove: (image: ContentImage) => void;
  onSelect: (image: ContentImage, url: string) => void;
  onManualPrompt: (image: ContentImage, prompt: string) => void;
}

function Gutter({ onClick, disabled, first }: { onClick: () => void; disabled: boolean; first?: boolean }) {
  return (
    <button
      type="button"
      className="ck-gutter"
      disabled={disabled}
      onClick={onClick}
      aria-label={first ? 'Insert image before the first block' : 'Insert image here'}
      title="Insert an image here"
    >
      <span className="ck-gutter-line" />
      <span className="ck-gutter-pill">
        <ImagePlus size={12} strokeWidth={2} />
        Insert image
      </span>
      <span className="ck-gutter-line" />
    </button>
  );
}

export default function PiecePreview({
  piece,
  html,
  images,
  featured,
  insertMode,
  busy,
  canEditSlots,
  onInsertAfter,
  onAddFeatured,
  onRemove,
  onSelect,
  onManualPrompt,
}: PiecePreviewProps) {
  const blocks = useMemo(() => parseArticle(html), [html]);
  const heading = useMemo(() => articleHeading(html), [html]);
  const bySlot = useMemo(() => {
    const map = new Map<string, ContentImage>();
    for (const img of images) map.set(img.slot_key, img);
    return map;
  }, [images]);

  const showGutters = insertMode && canEditSlots;

  return (
    <div className="ck-article-column">
      {/* featured image: lives above the title, outside the body html */}
      <div style={{ marginBottom: '22px' }}>
        {featured ? (
          <ImageSlot
            image={featured}
            altFallback={piece.title}
            busy={busy}
            onRemove={onRemove}
            onSelect={onSelect}
            onManualPrompt={onManualPrompt}
          />
        ) : canEditSlots ? (
          <button
            type="button"
            className="ck-featured-empty pressable"
            onClick={onAddFeatured}
            disabled={busy}
            aria-label="Add featured image"
          >
            <ImagePlus size={16} strokeWidth={1.75} />
            <span>Add featured image</span>
            <span className="ck-featured-hint">Prompt comes from the title and the opening of the article.</span>
          </button>
        ) : null}
      </div>

      {heading ? null : <h1 className="ck-article-title">{piece.title || 'Untitled'}</h1>}

      <div className="ck-article">
        {showGutters ? <Gutter first disabled={busy} onClick={() => onInsertAfter(-1, blocks)} /> : null}
        {blocks.map((block, i) => {
          if (block.kind === 'slot') {
            const image = bySlot.get(block.slotKey);
            return (
              <div key={`slot-${block.slotKey}-${i}`} className="ck-article-slot">
                {image ? (
                  <ImageSlot
                    image={image}
                    altFallback={piece.title}
                    busy={busy}
                    onRemove={onRemove}
                    onSelect={onSelect}
                    onManualPrompt={onManualPrompt}
                  />
                ) : block.placed ? (
                  // Placed figure whose row is gone: keep showing the image as the html has it.
                  <div dangerouslySetInnerHTML={{ __html: block.html }} />
                ) : (
                  <div className="ck-orphan-slot">
                    Image slot <code>{block.slotKey}</code> has no row. Save the article from Edit to clear it, or re-add the slot.
                  </div>
                )}
              </div>
            );
          }
          return (
            <div key={`block-${block.index}`} className="ck-article-block">
              <div dangerouslySetInnerHTML={{ __html: block.html }} />
              {showGutters ? <Gutter disabled={busy} onClick={() => onInsertAfter(block.index, blocks)} /> : null}
            </div>
          );
        })}
        {blocks.length === 0 ? (
          <p style={{ color: 'var(--text-faint)', fontSize: 'var(--text-sm)' }}>
            This piece has no body yet. The writer hands one in with <code>cockpit content put</code>.
          </p>
        ) : null}
      </div>
    </div>
  );
}
